import { Bullet, Circle, Label, p100, p50, percent, time } from "@amcharts/amcharts5";
import {
  AxisRenderer,
  AxisRendererX,
  AxisRendererY,
  CategoryAxis,
  ColumnSeries,
  DateAxis,
  ValueAxis,
  XYChart,
  XYCursor,
} from "@amcharts/amcharts5/xy";
import { cloneDeep, forIn } from "lodash";

import BaseChart, { LegendData } from "@/classes/BaseChart";
import { PLAN_MODULE } from "@/constants";
import { TIME_UNIT, getChartInterval } from "@/helpers/charts";
import { stringOrNumberToDate } from "@/helpers/formatters";
import Lang from "@/lang";
import { axisTitle } from "@/settings/charts";
import { DEFAULT_DATA_LABEL_SIZE } from "@/settings/charts/settingsDictionary";
import { FAMILY } from "@/settings/widgetFamilies";
import { chartFontSettings, chartInitialSettings, getAxisDateFormat } from "@/settings/widgets";
import { ChartDataRecord, TooltipSeries } from "@/types/charts";

export default class LollipopChart extends BaseChart {
  xAxis!: CategoryAxis<AxisRenderer> | DateAxis<AxisRenderer>;
  yAxis!: ValueAxis<AxisRenderer>;
  interval!: time.ITimeInterval;
  isDateAxis!: boolean;
  groups: string[] = [];

  initChart() {
    this.root.container.children.clear();

    if (this.chart) {
      (this.chart as XYChart).series.clear();
    }

    if (this.isDateAxis) {
      this.interval = getChartInterval(this.settings.decompositionMetric) as time.ITimeInterval;
    }

    this.createChart();
    this.createRenderers();
    this.createAxes();

    let legendNames: string[] = [];

    const series = this.isDateAxis ? [this.settings.displayMetric] : this.data;

    if (!this.isDateAxis) {
      legendNames = [...series];
    }

    series.forEach((el: ChartDataRecord, index: number) => {
      const paletteLength = this.palette.length;
      const indexColor = index % paletteLength;

      if (this.isDateAxis) {
        legendNames.push(el[this.settings.decompositionMetric] as string);
      }

      this.createSeries(el, indexColor);
    });
    this.showLegend(legendNames, (this.chart as XYChart).series.values as LegendData);
    this.createCursor();
  }

  protected createSeries(item: ChartDataRecord, index: number) {
    const seriesSettings = this.isDateAxis
      ? { valueXField: this.settings.decompositionMetric }
      : { categoryXField: this.settings.decompositionMetric };

    const series = (this.chart as XYChart).series.push(
      ColumnSeries.new(this.root, {
        fill: this.getSeriesColor(index),
        stroke: this.getSeriesColor(index),
        name: this.isDateAxis ? this.settings.displayMetric : (item[this.settings.decompositionMetric] as string),
        xAxis: this.xAxis,
        yAxis: this.yAxis,
        valueYField: this.settings.displayMetric,
        clustered: false,
        ...seriesSettings,
      })
    );

    series.columns.template.setAll({
      width: 0.5,
    });

    series.bullets.push(() => {
      return Bullet.new(this.root, {
        locationY: 1,
        sprite: Circle.new(this.root, {
          radius: 5,
          fill: series.get("fill"),
        }),
      });
    });

    if (this.settings.showDataLabels) {
      const labelFontSize = Number(this.settings.dataLabelsSizes ?? DEFAULT_DATA_LABEL_SIZE);
      const metricFormatter = this.formatters?.[this.settings.displayMetric];
      const formatterConfig = this.getDataLabelsFormatterConfig();

      const axis = series.get("yAxis") as ValueAxis<AxisRenderer> | undefined;
      const currentExtraMax = Number(axis?.get("extraMax") ?? 0);
      const requiredExtraMax = 0.1;
      if (axis && currentExtraMax < requiredExtraMax) {
        axis.set("extraMax", requiredExtraMax);
      }

      series.bullets.push((_root, _series, dataItem) => {
        const label = Label.new(this.root, {
          text: "",
          populateText: false,
          centerX: p50,
          dy: -32,
          fontSize: labelFontSize,
          fill: this.root.interfaceColors.get("text"),
        });

        label.adapters.add("text", () => {
          const rawValue = dataItem?.get("valueY");

          if (typeof rawValue !== "number" || !Number.isFinite(rawValue)) {
            return "";
          }

          if (metricFormatter) {
            return metricFormatter(rawValue, formatterConfig);
          }

          const metricFormatConfig = this.getMetricFormatter(this.settings.displayMetric, formatterConfig);
          return this.root.numberFormatter.format(rawValue, metricFormatConfig);
        });

        label.adapters.add("visible", () => {
          const rawValue = dataItem?.get("valueY");
          return typeof rawValue === "number" && Number.isFinite(rawValue);
        });

        return Bullet.new(this.root, {
          locationY: 1,
          sprite: label,
        });
      });
    }

    if (this.isDateAxis) {
      this.createLegendLabel(series, this.settings.displayMetric);
      series.data.setAll(this.data);
    } else {
      series.data.push(item);
    }

    this.createTooltip(series as TooltipSeries, () => {
      const param: Record<string, unknown>[] = [];
      const tooltipValues = series.get("tooltip")?.dataItem?.dataContext as Record<string, unknown>;
      if (tooltipValues) {
        forIn(tooltipValues, (value, key) => {
          param.push({ key, value });
        });
      }
      return param;
    });
  }

  protected createChart() {
    this.chart = this.root.container.children.push(
      XYChart.new(this.root, {
        ...chartInitialSettings,
        panX: true,
        panY: true,
        wheelX: "panX",
        wheelY: "zoomX",
        layout: this.layout,
      })
    );
  }

  protected createRenderers() {
    const x = AxisRendererX.new(this.root, {});

    x.labels.template.setAll({
      centerX: p50,
      centerY: p100,
      y: p100,
      ...chartFontSettings.label,
    });

    const y = AxisRendererY.new(this.root, {});
    y.labels.template.setAll({
      ...chartFontSettings.label,
    });

    this.renderers = { x, y };
  }

  protected createAxes() {
    this.xAxis = this.isDateAxis
      ? (this.chart as XYChart).xAxes.push(
          DateAxis.new(this.root, {
            renderer: this.renderers.x as AxisRendererX,
            groupData: true,
            groupIntervals: [this.interval],
            markUnitChange: false,
            baseInterval: { timeUnit: TIME_UNIT.DAY, count: 1 },
            dateFormats: getAxisDateFormat(this.settings.decompositionMetric),
          })
        )
      : (this.chart as XYChart).xAxes.push(
          CategoryAxis.new(this.root, {
            renderer: this.renderers.x as AxisRendererX,
            categoryField: this.settings.decompositionMetric,
          })
        );

    this.yAxis = (this.chart as XYChart).yAxes.push(
      ValueAxis.new(this.root, {
        renderer: this.renderers.y as AxisRenderer,
        numberFormat: this.getCompactAxesMetricFormatter(this.settings.displayMetric, this.data),
      })
    );

    if (this.settings.xAxisTitle) {
      this.xAxis.children.push(
        Label.new(this.root, {
          text: this.settings.xAxisTitle,
          ...chartFontSettings.axisTitle,
          x: p50,
          marginTop: axisTitle.marginTop,
          centerX: p50,
        })
      );
    }

    if (this.settings.yAxisTitle) {
      this.yAxis.children.unshift(
        Label.new(this.root, {
          text: this.settings.yAxisTitle,
          ...chartFontSettings.axisTitle,
          rotation: -90,
          y: percent(50),
          centerX: p50,
        })
      );
    }

    this.xAxis.data.setAll(this.data);
    this.yAxis.data.setAll(this.data);
  }

  mapData(data: ChartDataRecord[]): ChartDataRecord[] {
    this.isDateAxis = this.chartFamily === FAMILY.OVERTIME;
    let mappedData: ChartDataRecord[] = [];

    if (!Array.isArray(data)) {
      return [];
    }

    let preparedData: ChartDataRecord[] = cloneDeep(data);

    if (
      this.module === PLAN_MODULE &&
      this.settings?.showItems &&
      Number.isFinite(this.settings?.showItems) &&
      this.chartFamily !== FAMILY.OVERTIME
    ) {
      const decompositionMetric = this.settings.decompositionMetric;
      const displayMetric = this.settings.displayMetric;

      preparedData = this.prepareTopPlanModuleData(
        preparedData,
        decompositionMetric,
        displayMetric,
        this.settings.showItems
      );
    }

    if (this.isDateAxis) {
      mappedData = preparedData.map((el: ChartDataRecord) => ({
        ...el,
        [this.settings.decompositionMetric]: stringOrNumberToDate(
          el[this.settings.decompositionMetric] as string
        ).getTime(),
      }));
    } else {
      preparedData.forEach((el) => {
        mappedData.push({
          [this.settings.displayMetric]: el[this.settings.displayMetric] === null ? 0 : el[this.settings.displayMetric],
          [this.settings.decompositionMetric]:
            el[this.settings.decompositionMetric] === null ? Lang.null : el[this.settings.decompositionMetric],
        });
      });
    }

    return this.showItems(mappedData, this.settings.decompositionMetric, this.settings.displayMetric);
  }

  protected createCursor() {
    const cursor = (this.chart as XYChart).set(
      "cursor",
      XYCursor.new(this.root, {
        xAxis: this.xAxis,
        yAxis: this.yAxis,
      })
    );
    cursor.lineX.set("visible", true);
    cursor.lineY.set("visible", false);
  }
}
